/**
 * Règles de placement des constructions, partagées :
 * - le client les évalue en continu pour colorer le fantôme (vert/rouge) ;
 * - le serveur les réévalue à la réception de la commande (autorité).
 */
import { BUILDINGS, LIFTS, liftCost, type BuildingKind, type LiftKind } from './catalog';
import { distPointPolyline, distPointSegment, distSegmentSegment, type Vec2 } from './geometry';
import { computeLiftGeometry, topExit, type LiftGeometry } from './lifts';
import type { GameMap } from './map';
import type { Heightfield } from './terrain';
import type { EntityId } from './state';

export interface PlacementContext {
  hf: Heightfield;
  map: GameMap;
  lifts: Iterable<{ id: EntityId; kind: LiftKind; bottom: Vec2; top: Vec2 }>;
  pistes: Iterable<{ id: EntityId; path: Vec2[]; width: number }>;
  buildings: Iterable<{ id: EntityId; kind: BuildingKind; x: number; z: number; rot: number }>;
  cash?: number;
}

export type PlacementResult =
  | { ok: true; cost: number; pisteId?: EntityId; geometry?: LiftGeometry }
  | { ok: false; reason: string; cost: number };

export function buildingRadius(kind: BuildingKind): number {
  const [w, d] = BUILDINGS[kind].footprint;
  return Math.hypot(w, d) / 2;
}

/** Points d'accès au réseau (village, gares, arrivées de pistes). */
export function networkPoints(ctx: PlacementContext): Vec2[] {
  const pts: Vec2[] = [[ctx.map.features.village.x, ctx.map.features.village.z]];
  for (const l of ctx.lifts) {
    pts.push(l.bottom, l.top);
  }
  for (const p of ctx.pistes) {
    for (let i = 0; i < p.path.length; i += 4) pts.push(p.path[i] as Vec2);
    pts.push(p.path[p.path.length - 1] as Vec2);
  }
  return pts;
}

export function nearestPiste(ctx: PlacementContext, x: number, z: number): { id: EntityId; dist: number; width: number } | null {
  let best: { id: EntityId; dist: number; width: number } | null = null;
  for (const p of ctx.pistes) {
    const d = distPointPolyline([x, z], p.path);
    if (!best || d < best.dist) best = { id: p.id, dist: d, width: p.width };
  }
  return best;
}

/** Écart d'altitude sur l'emprise (5 points) rapporté à la diagonale. */
function footprintSlope(hf: Heightfield, x: number, z: number, rot: number, w: number, d: number): number {
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  const pts: Vec2[] = [[0, 0], [-w / 2, -d / 2], [w / 2, -d / 2], [w / 2, d / 2], [-w / 2, d / 2]];
  let lo = Infinity;
  let hi = -Infinity;
  for (const [px, pz] of pts) {
    const h = hf.height(x + px * c + pz * s, z - px * s + pz * c);
    lo = Math.min(lo, h);
    hi = Math.max(hi, h);
  }
  return (hi - lo) / Math.max(1, Math.hypot(w, d));
}

export function checkBuilding(ctx: PlacementContext, kind: BuildingKind, x: number, z: number, rot: number): PlacementResult {
  const spec = BUILDINGS[kind];
  const cost = spec.cost;
  const fail = (reason: string): PlacementResult => ({ ok: false, reason, cost });
  const r = buildingRadius(kind);
  const { hf, map } = ctx;
  // Les remontées sont parcourues deux fois : un itérateur de Map ne se relit pas.
  const lifts = [...ctx.lifts];
  if (!hf.inBounds(x, z, 40)) return fail('Hors de la carte');
  const lake = map.features.lake;
  if (Math.hypot(x - lake.x, z - lake.z) < lake.radius + r) return fail('Trop près du lac');
  const village = map.features.village;
  const dVillage = Math.hypot(x - village.x, z - village.z);
  if (spec.zone === 'village' && dVillage > map.villageZone) return fail('À construire dans le village');
  if (spec.zone === 'domain') {
    let near = dVillage < map.villageZone;
    for (const l of lifts) {
      if (Math.hypot(x - l.top[0], z - l.top[1]) < 200 || Math.hypot(x - l.bottom[0], z - l.bottom[1]) < 200) near = true;
    }
    if (!near) return fail("Trop loin d'une remontée (200 m max)");
  }
  let pisteId: EntityId | undefined;
  const np = nearestPiste(ctx, x, z);
  if (spec.zone === 'piste-side') {
    if (!np) return fail('Aucune piste à équiper');
    if (np.dist < np.width / 2 + 1) return fail('Sur la piste : placez-le en bordure');
    if (np.dist > np.width / 2 + 25) return fail('Trop loin de la piste (25 m max du bord)');
    pisteId = np.id;
  } else if (kind !== 'lamp_post' && np && np.dist < np.width / 2 + r * 0.7) {
    return fail('Empiète sur une piste');
  } else if (kind === 'lamp_post' && np && np.dist < np.width / 2) {
    return fail('Sur une piste');
  }
  if (footprintSlope(hf, x, z, rot, spec.footprint[0], spec.footprint[1]) > spec.maxSlope) return fail('Terrain trop pentu');
  for (const b of ctx.buildings) {
    const rb = buildingRadius(b.kind);
    const minD = kind === 'lamp_post' || b.kind === 'lamp_post' ? Math.max(r, rb) + 0.5 : (r + rb) * 0.85;
    if (Math.hypot(x - b.x, z - b.z) < minD) return fail('Chevauche une construction');
  }
  for (const l of lifts) {
    const clearance = kind === 'snow_cannon' || kind === 'lamp_post' ? 4 : r + 6;
    if (distPointSegment([x, z], l.bottom, l.top) < clearance) return fail('Sous une ligne de remontée');
    if (Math.hypot(x - l.bottom[0], z - l.bottom[1]) < 16 + r || Math.hypot(x - l.top[0], z - l.top[1]) < 16 + r) return fail('Trop près d\u2019une gare');
  }
  if (ctx.cash !== undefined && ctx.cash < cost) return fail('Fonds insuffisants');
  return pisteId !== undefined ? { ok: true, cost, pisteId } : { ok: true, cost };
}

export function checkLift(ctx: PlacementContext, kind: LiftKind, bottom: Vec2, top: Vec2): PlacementResult {
  const spec = LIFTS[kind];
  const { hf, map } = ctx;
  const horizontal = Math.hypot(top[0] - bottom[0], top[1] - bottom[1]);
  const cost = liftCost(kind, horizontal);
  const fail = (reason: string): PlacementResult => ({ ok: false, reason, cost });
  if (!hf.inBounds(bottom[0], bottom[1], 60) || !hf.inBounds(top[0], top[1], 60)) return fail('Hors de la carte');
  if (horizontal < spec.minLength) return fail(`Trop courte (${spec.minLength} m min)`);
  if (horizontal > spec.maxLength) return fail(`Trop longue (${spec.maxLength} m max)`);
  const rise = hf.height(top[0], top[1]) - hf.height(bottom[0], bottom[1]);
  if (rise < (kind === 'teleski' ? 20 : 40)) return fail("La gare amont doit être plus haute");
  if (rise / horizontal > spec.maxSlope) return fail('Ligne trop raide pour ce type');
  const lake = map.features.lake;
  if (distPointSegment([lake.x, lake.z], bottom, top) < lake.radius + 10) return fail('La ligne survole le lac');
  // Raccordement : la gare aval doit être accessible depuis le domaine.
  let connected = false;
  for (const p of networkPoints(ctx)) {
    if (Math.hypot(p[0] - bottom[0], p[1] - bottom[1]) < 160) {
      connected = true;
      break;
    }
  }
  if (!connected) return fail('Gare aval non raccordée au domaine (160 m max)');
  for (const l of ctx.lifts) {
    if (distSegmentSegment(bottom, top, l.bottom, l.top) < 18) return fail('Croise une autre remontée');
  }
  for (const b of ctx.buildings) {
    if (b.kind === 'lamp_post' || b.kind === 'snow_cannon') continue;
    const r = buildingRadius(b.kind);
    if (distPointSegment([b.x, b.z], bottom, top) < r + 8) return fail('Une construction gêne la ligne');
  }
  const geometry = computeLiftGeometry(hf, kind, bottom, top);
  const exit = topExit(geometry);
  if (!hf.inBounds(exit[0], exit[1], 30)) return fail('Gare amont trop près du bord');
  if (ctx.cash !== undefined && ctx.cash < cost) return fail('Fonds insuffisants');
  return { ok: true, cost, geometry };
}
